import { FC, useMemo, useState } from 'react';
import {
    useTranslate,
    IResourceComponentsProps,
    HttpError,
    useNavigation,
} from '@refinedev/core';
import { Form, Table } from 'antd';
import {
    TextField,
    getDefaultSortOrder,
    useTable,
    useSelect,
} from '@refinedev/antd';
import { IRecord, IRecordFilterVariables, IApplication } from 'interfaces';
import { api } from '../../../utils/api';
import { useNotification } from '../../../hooks/useNotification';
import { InternalLayout } from 'layouts/InternalLayout';
import { TranslatedBreadcrumbs } from 'components/translated-breadcrumbs';
import { Button } from 'components/ui/button';
import TablePagination from 'components/ui/table-pagination';
import {
    FilterControls,
    type FilterDropdownOption,
} from 'components/applications/filter-controls';
import { Heading } from 'components/ui/heading';
import styles from './list.module.scss';

interface IDictionaryKey {
    dictionary_name: string;
}

export const PharmasolList: FC<IResourceComponentsProps> = () => {
    const t = useTranslate();
    const { show } = useNavigation();
    const { msgSuccess } = useNotification();
    const [filtersForm] = Form.useForm();
    const [dictKey, setDictKey] = useState<IDictionaryKey | undefined>();
    const [isSyncing, setIsSyncing] = useState(false);
    const {
        tableProps,
        sorters: sorter,
        tableQueryResult,
        current,
        setCurrent,
        pageSize,
        setPageSize,
    } = useTable<IApplication, HttpError, IRecordFilterVariables>({
        resource: 'pharmasol/dictionaries/sync',
    });

    const { selectProps: dictionarySyncProps } = useSelect<IRecord>({
        resource: 'pharmasol/dictionaries/sync',
        defaultValue: [],
        optionValue: 'dictionary_name',
        optionLabel: 'dictionary_name',
        pagination: {
            mode: 'server',
        },
    });

    const dictionaryOptions = useMemo<FilterDropdownOption[]>(() => {
        return (
            dictionarySyncProps?.options?.map((option: any) => ({
                value: option.value,
                label: t(`labels.${option.label}`),
            })) ?? []
        );
    }, [dictionarySyncProps?.options, t]);

    const handleSync = async () => {
        try {
            setIsSyncing(true);
            const { status } = await api.post(
                `/api/pharmasol/dictionaries/sync`,
                null,
                {
                    params: {
                        ...dictKey,
                    },
                }
            );

            if (status === 200) {
                msgSuccess(t('pharmasol.message.synced'));
                await tableQueryResult?.refetch?.();
            }
        } catch (e) {
            console.error(e);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleChange = (value: any): void => {
        if (value === undefined || value === null || value === '') {
            setDictKey(undefined);
            return;
        }

        setDictKey({
            dictionary_name: value,
        });
    };

    const handleResetFilters = () => {
        setDictKey(undefined);
        filtersForm.resetFields();
    };

    const isAllActive = !dictKey;

    const {
        pagination: rawPagination,
        loading: tableLoading,
        ...restTableProps
    } = tableProps;
    const pagination =
        rawPagination === false ? undefined : rawPagination ?? undefined;

    const totalItems = tableQueryResult?.data?.total ?? pagination?.total ?? 0;
    const currentPage = current ?? pagination?.current ?? 1;
    const resolvedPageSize = pageSize ?? pagination?.pageSize ?? 10;

    const defaultPageSizeOptions = ['10', '25', '50', '100'];
    const rawPageSizeOptions = (
        pagination?.pageSizeOptions ?? defaultPageSizeOptions
    ).map(option => option.toString());

    const uniquePageSizeOptions = rawPageSizeOptions.reduce<string[]>(
        (acc, current) => {
            if (!acc.includes(current)) acc.push(current);
            return acc;
        },
        []
    );

    const orderedPageSizeOptions = defaultPageSizeOptions.filter(option =>
        uniquePageSizeOptions.includes(option)
    );

    uniquePageSizeOptions.forEach(option => {
        if (!orderedPageSizeOptions.includes(option)) {
            orderedPageSizeOptions.push(option);
        }
    });

    if (!orderedPageSizeOptions.includes(resolvedPageSize.toString())) {
        orderedPageSizeOptions.push(resolvedPageSize.toString());
    }

    const handlePageChange = (page: number) => {
        setCurrent?.(page);
    };

    const handlePageSizeChange = (value: number) => {
        if (Number.isNaN(value)) return;

        setPageSize?.(value);
        setCurrent?.(1);
    };

    const dropdowns = [
        {
            placeholder: t('select_dictionary'),
            value: dictKey?.dictionary_name,
            options: dictionaryOptions,
            onSelect: (value: string | number | undefined) =>
                handleChange(value),
            clearLabel: t('applications.filters.clear'),
        },
    ];

    return (
        <InternalLayout
            breadcrumbs={<TranslatedBreadcrumbs />}
            hideSidebar
            hideHeaderDivider
            noPadding
        >
            <div className={styles.pageWrapper}>
                <header className={styles.header}>
                    <Heading level={1} align="center" className={styles.title}>
                        {t('pharmasol.dictionaries')}
                    </Heading>
                    <FilterControls
                        formProps={{ form: filtersForm }}
                        searchPlaceholder={t(
                            'pharmasol.fields.dictionary_name',
                            'Dictionary'
                        )}
                        filtersLabel={t('applications.titles.filters')}
                        clearFilterLabel={t('applications.filters.clear')}
                        isAllActive={isAllActive}
                        onResetFilters={handleResetFilters}
                        hideSearchField
                        inlineFiltersFullWidth
                        showFilterToggle={false}
                        dropdownsGrow={false}
                        dropdowns={dropdowns}
                        inlineFilters={
                            <div className={styles.inlineFilters}>
                                <Button
                                    onClick={handleSync}
                                    variant="primary"
                                    size="small"
                                    className={styles.syncButton}
                                    loading={isSyncing}
                                >
                                    {t('pharmasol.buttons.sync')}
                                </Button>
                            </div>
                        }
                    />
                </header>
                <div className={styles.tableWrapper}>
                    <Table
                        {...restTableProps}
                        loading={tableLoading || isSyncing}
                        pagination={false}
                        rowKey="id"
                        className={styles.table}
                        scroll={{ x: 500 }}
                    >
                        <Table.Column
                            dataIndex="dictionary_name"
                            key="dictionary_name"
                            title={t('pharmasol.fields.code')}
                            render={value => <TextField value={value} />}
                            defaultSortOrder={getDefaultSortOrder(
                                'dictionary_name',
                                sorter
                            )}
                            sorter
                        />
                        <Table.Column
                            dataIndex="dictionary_name"
                            key="dictionary_name_name"
                            title={t('pharmasol.fields.name')}
                            render={value => t(`pharmasol.fields.${value}`)}
                        />
                        <Table.Column
                            dataIndex="lines"
                            key="lines"
                            title={t('pharmasol.fields.lines')}
                            render={value => <TextField value={value} />}
                            defaultSortOrder={getDefaultSortOrder(
                                'lines',
                                sorter
                            )}
                            sorter
                        />
                        <Table.Column
                            dataIndex="last_updates"
                            key="last_updates"
                            title={t('pharmasol.fields.last_updates')}
                            defaultSortOrder={getDefaultSortOrder(
                                'last_updates',
                                sorter
                            )}
                            sorter
                        />
                        <Table.Column<IRecord>
                            title={t('table.actions')}
                            dataIndex="actions"
                            render={(_, record: any) => {
                                const dictionary = record.dictionary_name;

                                return (
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        onClick={() =>
                                            show('dictionaries', dictionary)
                                        }
                                    >
                                        {t('buttons.show')}
                                    </Button>
                                );
                            }}
                            align="center"
                        />
                    </Table>
                </div>
                {totalItems ? (
                    <TablePagination
                        currentPage={currentPage}
                        pageSize={resolvedPageSize}
                        totalItems={totalItems}
                        pageSizeOptions={orderedPageSizeOptions}
                        onChangePage={handlePageChange}
                        onChangePageSize={handlePageSizeChange}
                        previousLabel={t('applications.pagination.previous')}
                        nextLabel={t('applications.pagination.next')}
                        showDocumentsLabel={t(
                            'applications.pagination.show_documents'
                        )}
                        rangeLabel={(start, end, total) =>
                            `${start}-${end} ${t(
                                'applications.pagination.of_total'
                            )} ${total}`
                        }
                        className={styles.pagination}
                    />
                ) : null}
            </div>
        </InternalLayout>
    );
};
